/**
 * Reporting V2 Service
 *
 * Service layer for Reporting V2 API
 * Handles dashboards, reports, schedules, and subscriptions
 *
 * @version 2.0
 * @date 2025-10-07
 */

import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import type {
  // Dashboard types
  AdminOverviewRequest,
  AdminOverviewResponse,
  ExecutiveSummaryRequest,
  ExecutiveSummaryResponse,
  ManagerTeamRequest,
  ManagerTeamResponse,
  LearnerProgressRequest,
  LearnerProgressResponse,
  DepartmentPerformanceRequest,
  DepartmentPerformanceResponse,
  CourseAnalyticsRequest,
  CourseAnalyticsResponse,

  // Report types
  OrganizationOverviewRequest,
  OrganizationOverviewReport,
  DepartmentPerformanceReportRequest,
  DepartmentPerformanceReport,
  RoleAnalysisRequest,
  CourseEffectivenessRequest,
  ListReportsQuery,
  ListReportsResponse,
  LearnerProgressQuery,
  LearnerProgressReport,
  CertificationsQuery,
  CertificationsReport,

  // Schedule types
  CreateScheduleRequest,
  Schedule,
  ListSchedulesQuery,
  PaginatedResponse,
  ExecuteScheduleRequest,
  ExecutionResult,
  ExecutionHistoryQuery,
  ExecutionHistoryResponse,
  DeleteResponse,

  // Subscription types
  CreateSubscriptionRequest,
  Subscription,
  ListSubscriptionsQuery,
  SendNowRequest,
  SendNowResponse,
  DeliveryHistoryQuery,
  DeliveryHistoryResponse,
} from '@/types/reporting-v2';

class ReportingService {
  // ============================================================================
  // Dashboard Methods
  // ============================================================================

  /**
   * Get Admin Overview Dashboard
   * Real-time metrics with 30 min cache
   */
  async getAdminOverview(params: AdminOverviewRequest): Promise<AdminOverviewResponse['data']> {
    try {
      const response = await apiClient.get<AdminOverviewResponse>(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.ADMIN_OVERVIEW,
        { params }
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching admin overview dashboard:', error);
      throw error;
    }
  }

  /**
   * Get Executive Summary Dashboard
   * High-level KPIs for leadership with 30 min cache
   */
  async getExecutiveSummary(params: ExecutiveSummaryRequest): Promise<ExecutiveSummaryResponse['data']> {
    try {
      const response = await apiClient.get<ExecutiveSummaryResponse>(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.EXECUTIVE_SUMMARY,
        { params }
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching executive summary dashboard:', error);
      throw error;
    }
  }

  /**
   * Get Manager Team Dashboard
   * Team performance metrics with 15 min cache
   */
  async getManagerTeam(params: ManagerTeamRequest): Promise<ManagerTeamResponse['data']> {
    try {
      const response = await apiClient.get<ManagerTeamResponse>(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.MANAGER_TEAM,
        { params }
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching manager team dashboard:', error);
      throw error;
    }
  }

  /**
   * Get Learner Progress Dashboard
   * Personal learning journey with 5 min cache
   */
  async getLearnerProgress(params: LearnerProgressRequest): Promise<LearnerProgressResponse['data']> {
    try {
      const response = await apiClient.get<LearnerProgressResponse>(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.LEARNER_PROGRESS,
        { params }
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching learner progress dashboard:', error);
      throw error;
    }
  }

  /**
   * Get Department Performance Dashboard
   * Department-level analytics with 20 min cache
   */
  async getDepartmentPerformance(params: DepartmentPerformanceRequest): Promise<DepartmentPerformanceResponse['data']> {
    try {
      const { department_id, ...queryParams } = params;
      if (!department_id) {
        throw new Error('department_id is required for department performance dashboard');
      }
      const response = await apiClient.get<DepartmentPerformanceResponse>(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.DEPARTMENT_PERFORMANCE(department_id),
        { params: queryParams }
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching department performance dashboard:', error);
      throw error;
    }
  }

  /**
   * Get Course Analytics Dashboard
   * Individual course performance with 20 min cache
   */
  async getCourseAnalytics(params: CourseAnalyticsRequest): Promise<CourseAnalyticsResponse['data']> {
    try {
      const { course_id, ...queryParams } = params;
      if (!course_id) {
        throw new Error('course_id is required for course analytics dashboard');
      }
      const response = await apiClient.get<CourseAnalyticsResponse>(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.COURSE_ANALYTICS(course_id),
        { params: queryParams }
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching course analytics dashboard:', error);
      throw error;
    }
  }

  /**
   * Refresh All Dashboards
   * Force refresh all dashboard caches
   */
  async refreshAllDashboards(): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.REFRESH_ALL
      );
      return response.data;
    } catch (error) {
      console.error('Error refreshing all dashboards:', error);
      throw error;
    }
  }

  /**
   * Get Cache Statistics
   * Retrieve cache stats for all dashboards
   */
  async getCacheStats(): Promise<any> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.DASHBOARDS.CACHE_STATS
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching cache stats:', error);
      throw error;
    }
  }

  // ============================================================================
  // Report Generation Methods
  // ============================================================================

  /**
   * Generate Organization Overview Report
   * Company-wide training metrics snapshot
   */
  async generateOrganizationOverview(params: OrganizationOverviewRequest): Promise<OrganizationOverviewReport> {
    try {
      const response = await apiClient.post<{ success: true; data: OrganizationOverviewReport }>(
        API_ENDPOINTS.REPORTING_V2.REPORTS.GENERATE_ORGANIZATION_OVERVIEW,
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error generating organization overview report:', error);
      throw error;
    }
  }

  /**
   * Generate Department Performance Report
   * Detailed department analysis
   */
  async generateDepartmentPerformance(params: DepartmentPerformanceReportRequest): Promise<DepartmentPerformanceReport> {
    try {
      const response = await apiClient.post<{ success: true; data: DepartmentPerformanceReport }>(
        API_ENDPOINTS.REPORTING_V2.REPORTS.GENERATE_DEPARTMENT_PERFORMANCE,
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error generating department performance report:', error);
      throw error;
    }
  }

  /**
   * Generate Role Analysis Report
   * Performance by role/job function
   */
  async generateRoleAnalysis(params: RoleAnalysisRequest): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.REPORTS.GENERATE_ROLE_ANALYSIS,
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error generating role analysis report:', error);
      throw error;
    }
  }

  /**
   * Generate Course Effectiveness Report
   * Individual course performance analysis
   */
  async generateCourseEffectiveness(params: CourseEffectivenessRequest): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.REPORTS.GENERATE_COURSE_EFFECTIVENESS,
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error generating course effectiveness report:', error);
      throw error;
    }
  }


  /**
   * Preview Report Structure
   * Returns preview of report structure without full computation
   */
  async previewReport(reportType: string): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.REPORTS.PREVIEW(reportType)
      );
      return response.data;
    } catch (error) {
      console.error('Error previewing report:', error);
      throw error;
    }
  }

  /**
   * Export Report
   * Download report in specified format (pdf, excel, csv)
   */
  async exportReport(snapshotId: string, format: 'pdf' | 'excel' | 'csv'): Promise<any> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.REPORTS.EXPORT(snapshotId, format)
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting report:', error);
      throw error;
    }
  }

  /**
   * Get Audit Trail
   * Retrieve audit trail of report generation activities
   */
  async getAuditTrail(query?: any): Promise<any> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.REPORTS.AUDIT_TRAIL,
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching audit trail:', error);
      throw error;
    }
  }

  // ============================================================================
  // Report Management Methods
  // ============================================================================

  /**
   * List Generated Reports
   * Paginated list with filters
   */
  async listReports(query: ListReportsQuery): Promise<ListReportsResponse['data']> {
    try {
      const response = await apiClient.get<ListReportsResponse>(
        API_ENDPOINTS.REPORTING_V2.REPORTS.LIST,
        { params: query }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error listing reports:', error);
      throw error;
    }
  }

  /**
   * Get Report by Snapshot ID
   * Retrieve full report details
   */
  async getReport(snapshotId: string): Promise<any> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.REPORTS.GET_BY_ID(snapshotId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching report:', error);
      throw error;
    }
  }

  /**
   * Delete Report
   * Remove specific report snapshot
   */
  async deleteReport(snapshotId: string): Promise<DeleteResponse> {
    try {
      const response = await apiClient.delete<DeleteResponse>(
        API_ENDPOINTS.REPORTING_V2.REPORTS.DELETE(snapshotId)
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting report:', error);
      throw error;
    }
  }

  /**
   * Export Report to PDF
   * Download report as PDF file
   */
  async exportReportPDF(snapshotId: string): Promise<Blob> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.REPORTS.EXPORT_PDF(snapshotId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting report to PDF:', error);
      throw error;
    }
  }

  /**
   * Export Report to Excel
   * Download report as Excel file
   */
  async exportReportExcel(snapshotId: string): Promise<Blob> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.REPORTS.EXPORT_EXCEL(snapshotId),
        { responseType: 'blob' }
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting report to Excel:', error);
      throw error;
    }
  }

  // ============================================================================
  // Learner Report Methods
  // ============================================================================

  /**
   * Generate Learner Progress Report
   * Individual learning journey report
   */
  async generateLearnerProgressReport(params: any): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.LEARNER_REPORTS.PROGRESS,
        params
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error generating learner progress report:', error);
      throw error;
    }
  }

  /**
   * Generate Certifications Report
   * Certification tracking
   */
  async generateCertificationsReport(params: any): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.LEARNER_REPORTS.CERTIFICATIONS,
        params
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error generating certifications report:', error);
      throw error;
    }
  }

  /**
   * Generate Learning Path Report
   * Progress on enrolled learning paths
   */
  async generateLearningPathReport(params: any): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.LEARNER_REPORTS.LEARNING_PATH,
        params
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error generating learning path report:', error);
      throw error;
    }
  }

  /**
   * Generate Skill Assessment Report
   * Skill gaps and assessment scores
   */
  async generateSkillAssessmentReport(params: any): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.LEARNER_REPORTS.SKILL_ASSESSMENT,
        params
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error generating skill assessment report:', error);
      throw error;
    }
  }

  /**
   * Generate Peer Comparison Report
   * Anonymous benchmarking with peers
   */
  async generatePeerComparisonReport(params: any): Promise<any> {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.REPORTING_V2.LEARNER_REPORTS.PEER_COMPARISON,
        params
      );
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error generating peer comparison report:', error);
      throw error;
    }
  }

  /**
   * Get My Reports
   * Retrieve all reports for authenticated learner
   */
  async getMyReports(query?: any): Promise<any[]> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.LEARNER_REPORTS.MY_REPORTS,
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching my reports:', error);
      throw error;
    }
  }

  /**
   * Get My Report by ID
   * Retrieve specific learner report
   */
  async getMyReportById(reportId: string): Promise<any> {
    try {
      const response = await apiClient.get(
        API_ENDPOINTS.REPORTING_V2.LEARNER_REPORTS.GET_BY_ID(reportId)
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching learner report:', error);
      throw error;
    }
  }

  // ============================================================================
  // Schedule Management Methods
  // ============================================================================

  /**
   * List Schedules
   * Get all schedules with filters
   */
  async listSchedules(query: ListSchedulesQuery): Promise<PaginatedResponse<Schedule>['data']> {
    try {
      const response = await apiClient.get<any>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.LIST,
        { params: query }
      );

      // Handle response - backend returns array directly in data
      const schedules = Array.isArray(response.data.data) ? response.data.data : [];

      // Create paginated response structure
      return {
        schedules,
        pagination: {
          page: query.page || 1,
          limit: query.limit || 12,
          total: schedules.length,
          total_pages: 1,
        },
      };
    } catch (error) {
      console.error('Error listing schedules:', error);
      throw error;
    }
  }

  /**
   * Get Schedule by ID
   * Retrieve schedule details
   */
  async getSchedule(scheduleId: string): Promise<Schedule> {
    try {
      const response = await apiClient.get<{ success: true; data: Schedule }>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.GET_BY_ID(scheduleId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching schedule:', error);
      throw error;
    }
  }

  /**
   * Create Schedule
   * Create new automated report schedule
   */
  async createSchedule(params: CreateScheduleRequest): Promise<Schedule> {
    try {
      const response = await apiClient.post<{ success: true; data: Schedule }>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.CREATE,
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating schedule:', error);
      throw error;
    }
  }

  /**
   * Update Schedule
   * Update existing schedule configuration
   */
  async updateSchedule(scheduleId: string, params: Partial<CreateScheduleRequest>): Promise<Schedule> {
    try {
      const response = await apiClient.put<{ success: true; data: Schedule }>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.UPDATE(scheduleId),
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating schedule:', error);
      throw error;
    }
  }

  /**
   * Delete Schedule
   * Remove schedule
   */
  async deleteSchedule(scheduleId: string): Promise<DeleteResponse> {
    try {
      const response = await apiClient.delete<DeleteResponse>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.DELETE(scheduleId)
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting schedule:', error);
      throw error;
    }
  }

  /**
   * Execute Schedule
   * Manually trigger schedule execution
   */
  async executeSchedule(scheduleId: string, params?: ExecuteScheduleRequest): Promise<ExecutionResult> {
    try {
      const response = await apiClient.post<{ success: true; data: ExecutionResult }>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.EXECUTE(scheduleId),
        params || {}
      );
      return response.data.data;
    } catch (error) {
      console.error('Error executing schedule:', error);
      throw error;
    }
  }

  /**
   * Pause Schedule
   * Temporarily pause schedule execution
   */
  async pauseSchedule(scheduleId: string): Promise<Schedule> {
    try {
      const response = await apiClient.post<{ success: true; data: Schedule }>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.PAUSE(scheduleId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Error pausing schedule:', error);
      throw error;
    }
  }

  /**
   * Resume Schedule
   * Resume paused schedule
   */
  async resumeSchedule(scheduleId: string): Promise<Schedule> {
    try {
      const response = await apiClient.post<{ success: true; data: Schedule }>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.RESUME(scheduleId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Error resuming schedule:', error);
      throw error;
    }
  }

  /**
   * Get Execution History
   * Retrieve schedule execution history
   */
  async getExecutionHistory(scheduleId: string, query: ExecutionHistoryQuery): Promise<ExecutionHistoryResponse['data']> {
    try {
      const response = await apiClient.get<ExecutionHistoryResponse>(
        API_ENDPOINTS.REPORTING_V2.SCHEDULES.EXECUTIONS(scheduleId),
        { params: query }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching execution history:', error);
      throw error;
    }
  }

  // ============================================================================
  // Subscription Management Methods
  // ============================================================================

  /**
   * List Subscriptions
   * Get all subscriptions with filters
   */
  async listSubscriptions(query: ListSubscriptionsQuery): Promise<PaginatedResponse<Subscription>['data']> {
    try {
      const response = await apiClient.get<PaginatedResponse<Subscription>>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.LIST,
        { params: query }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error listing subscriptions:', error);
      throw error;
    }
  }

  /**
   * Get Subscription by ID
   * Retrieve subscription details
   */
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    try {
      const response = await apiClient.get<{ success: true; data: Subscription }>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.GET_BY_ID(subscriptionId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  }

  /**
   * Create Subscription
   * Create new email subscription
   */
  async createSubscription(params: CreateSubscriptionRequest): Promise<Subscription> {
    try {
      const response = await apiClient.post<{ success: true; data: Subscription }>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.CREATE,
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  /**
   * Update Subscription
   * Update existing subscription configuration
   */
  async updateSubscription(subscriptionId: string, params: Partial<CreateSubscriptionRequest>): Promise<Subscription> {
    try {
      const response = await apiClient.put<{ success: true; data: Subscription }>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.UPDATE(subscriptionId),
        params
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  /**
   * Delete Subscription
   * Remove subscription
   */
  async deleteSubscription(subscriptionId: string): Promise<DeleteResponse> {
    try {
      const response = await apiClient.delete<DeleteResponse>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.DELETE(subscriptionId)
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting subscription:', error);
      throw error;
    }
  }

  /**
   * Send Subscription Now
   * Manually trigger subscription delivery
   */
  async sendSubscriptionNow(subscriptionId: string, params?: SendNowRequest): Promise<SendNowResponse['data']> {
    try {
      const response = await apiClient.post<SendNowResponse>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.SEND_NOW(subscriptionId),
        params || {}
      );
      return response.data.data;
    } catch (error) {
      console.error('Error sending subscription:', error);
      throw error;
    }
  }

  /**
   * Pause Subscription
   * Temporarily pause subscription delivery
   */
  async pauseSubscription(subscriptionId: string): Promise<Subscription> {
    try {
      const response = await apiClient.post<{ success: true; data: Subscription }>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.PAUSE(subscriptionId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Error pausing subscription:', error);
      throw error;
    }
  }

  /**
   * Resume Subscription
   * Resume paused subscription
   */
  async resumeSubscription(subscriptionId: string): Promise<Subscription> {
    try {
      const response = await apiClient.post<{ success: true; data: Subscription }>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.RESUME(subscriptionId)
      );
      return response.data.data;
    } catch (error) {
      console.error('Error resuming subscription:', error);
      throw error;
    }
  }

  /**
   * Get Delivery History
   * Retrieve subscription delivery history
   */
  async getDeliveryHistory(subscriptionId: string, query: DeliveryHistoryQuery): Promise<DeliveryHistoryResponse['data']> {
    try {
      const response = await apiClient.get<DeliveryHistoryResponse>(
        API_ENDPOINTS.REPORTING_V2.SUBSCRIPTIONS.DELIVERIES(subscriptionId),
        { params: query }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error fetching delivery history:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const reportingService = new ReportingService();
export default reportingService;
